import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, Close, Refresh, Visibility } from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES, ROLE_DISPLAY_NAMES } from '../../constants/roles';
import {
  FORM_REFS,
  STATUS_COLORS,
  STATUS_LABELS,
  emptyCompetencyRows
} from '../../constants/certificationAuthorization';
import GetsCm008ApplicationForm, {
  emptyGetsCm008Form
} from './GetsCm008ApplicationForm';
import GetsCm003CompetencyForm from './GetsCm003CompetencyForm';
import GetsCm001CertificateForm, {
  emptyGetsCm001Certificate
} from './GetsCm001CertificateForm';
import GetsBrandHeader from './GetsBrandHeader';
import { downloadAuthenticatedPdf } from '../../utils/authenticatedPdf';
import {
  ATTACHMENT_SECTIONS,
  deleteApplicationAttachment,
  deleteRowAttachment,
  deleteSectionAttachment,
  downloadApplicationAttachment,
  emptyPendingRowFiles,
  emptyPendingSectionFiles,
  getSectionAttachmentsFromDoc,
  rowPendingKey,
  uploadApplicationAttachments,
  uploadPendingRowAttachments,
  uploadRowAttachments,
  uploadSectionAttachments,
  viewApplicationAttachment
} from '../../utils/authorizationAttachments';

const API_BASE = '/api/certification-authorization';

const boardUserFilter = createFilterOptions({
  stringify: (option) =>
    `${option.firstName || ''} ${option.lastName || ''} ${option.email || ''} ${option.role || ''} ${ROLE_DISPLAY_NAMES[option.role] || ''}`
});

const formatBoardUserLabel = (user) => {
  if (!user) return '';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'User';
  const roleLabel = ROLE_DISPLAY_NAMES[user.role] || user.role || '';
  return roleLabel ? `${name} (${roleLabel})` : name;
};

/** Keep board candidate dropdowns wide enough to show full "Name (Role)" labels. */
const boardAutocompleteWideProps = {
  fullWidth: true,
  ListboxProps: {
    sx: {
      maxHeight: 360,
      '& .MuiAutocomplete-option': {
        whiteSpace: 'nowrap',
        display: 'block',
        overflow: 'visible'
      }
    }
  },
  slotProps: {
    popper: {
      sx: {
        zIndex: (theme) => theme.zIndex.modal + 2,
        width: 'auto !important',
        minWidth: 'var(--board-ac-anchor-width, 100%)',
        maxWidth: 'min(960px, calc(100vw - 32px))',
        '& .MuiAutocomplete-paper': {
          width: 'max-content',
          minWidth: '100%',
          maxWidth: 'min(960px, calc(100vw - 32px))'
        }
      }
    }
  },
  sx: { width: '100%', minWidth: { xs: 0, sm: 520 } }
};

const emptyForm = (user) =>
  emptyGetsCm008Form({
    applicantName: user
      ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
      : '',
    applicantDeclarationDate: dayjs().format('YYYY-MM-DD')
  });

/**
 * @param {object} props
 * @param {boolean} [props.embedded] — nest inside profile / MRO workbench (compact chrome)
 * @param {'full'|'crew'|'mro'} [props.variant]
 */
function CertificationAuthorizationPage({ embedded = false, variant = 'full' } = {}) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [docTab, setDocTab] = useState(0);
  const [form, setForm] = useState(() => emptyForm());
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingSectionFiles, setPendingSectionFiles] = useState(() => emptyPendingSectionFiles());
  const [pendingRowFiles, setPendingRowFiles] = useState(() => emptyPendingRowFiles());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm((prev) => ({
      ...prev,
      applicantName: prev.applicantName || `${user.firstName || ''} ${user.lastName || ''}`.trim()
    }));
  }, [user]);

  // MRO Manager decide
  const [managerDecision, setManagerDecision] = useState({
    decision: 'ACCEPTED',
    remarks: ''
  });

  // MRO QM decide
  const [mroDecision, setMroDecision] = useState({
    decision: 'ACCEPTED',
    remarks: '',
    checkedLicences: true,
    checkedTrainings: true,
    checkedExperience: true,
    checkedCompanyApproval: true,
    competencyAssessmentRequired: true
  });

  // Board
  const [boardCandidates, setBoardCandidates] = useState([]);
  const [boardForm, setBoardForm] = useState({
    seat2Role: 'LMM',
    seat2Name: '',
    seat2UserId: '',
    seat2User: null,
    seat3Name: '',
    seat3UserId: '',
    seat3User: null,
    seat3AmeLic: ''
  });


  const perms = meta?.permissions || {};
  const canApply = perms.canApply;
  const canMroManager = Boolean(perms.canMroManagerReview);
  const canMroQm = perms.canMroQmReview || perms.canMroQmManage;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [metaRes, listRes] = await Promise.all([
        axios.get('/api/certification-authorization/meta'),
        axios.get('/api/certification-authorization')
      ]);
      setMeta(metaRes.data);
      setRows(listRes.data || []);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load certification authorizations';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const loadBoardCandidates = useCallback(async () => {
    if (!canMroQm) return;
    try {
      const res = await axios.get('/api/certification-authorization/board-candidates');
      setBoardCandidates(res.data || []);
    } catch (err) {
      console.error('Failed to load board candidates', err);
    }
  }, [canMroQm]);

  useEffect(() => {
    if (canMroQm) loadBoardCandidates();
  }, [canMroQm, loadBoardCandidates]);

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`/api/certification-authorization/${id}`);
      setDocTab(0);
      setDetail(res.data);
      if (canMroQm) loadBoardCandidates();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load application', 'error');
    }
  };

  const applyBoardUserSelection = (seatKey, value) => {
    if (seatKey === 'seat2') {
      if (!value) {
        setBoardForm((b) => ({ ...b, seat2User: null, seat2UserId: '', seat2Name: '' }));
        return;
      }
      if (typeof value === 'string') {
        setBoardForm((b) => ({ ...b, seat2User: null, seat2UserId: '', seat2Name: value }));
        return;
      }
      setBoardForm((b) => ({
        ...b,
        seat2User: value,
        seat2UserId: value._id,
        seat2Name: `${value.firstName || ''} ${value.lastName || ''}`.trim()
      }));
      return;
    }
    if (!value) {
      setBoardForm((b) => ({ ...b, seat3User: null, seat3UserId: '', seat3Name: '' }));
      return;
    }
    if (typeof value === 'string') {
      setBoardForm((b) => ({ ...b, seat3User: null, seat3UserId: '', seat3Name: value }));
      return;
    }
    setBoardForm((b) => ({
      ...b,
      seat3User: value,
      seat3UserId: value._id,
      seat3Name: `${value.firstName || ''} ${value.lastName || ''}`.trim()
    }));
  };

  const handleCreate = async () => {
    if (!form.applicantName?.trim()) {
      showToast('Name of the Applicant is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        qualification: {
          ...(form.qualification || {}),
          validity: form.qualification?.validity || undefined
        }
      };
      delete payload.applicationAttachments;
      const res = await axios.post(API_BASE, payload);
      let created = res.data;
      try {
        if (pendingFiles.length) {
          const withAttachments = await uploadApplicationAttachments(
            API_BASE,
            created._id,
            pendingFiles
          );
          if (withAttachments) created = withAttachments;
        }
        for (const section of ATTACHMENT_SECTIONS) {
          const files = pendingSectionFiles[section] || [];
          if (!files.length) continue;
          const withSection = await uploadSectionAttachments(
            API_BASE,
            created._id,
            section,
            files
          );
          if (withSection) created = withSection;
        }
        if (Object.keys(pendingRowFiles).length) {
          const withRows = await uploadPendingRowAttachments(
            API_BASE,
            created._id,
            created,
            pendingRowFiles
          );
          if (withRows) created = withRows;
        }
      } catch (uploadErr) {
        showToast(
          uploadErr.response?.data?.message || 'Draft created, but some attachments failed to upload',
          'warning'
        );
      }
      showToast('Draft application created (GETS/CM/008)', 'success');
      setCreateOpen(false);
      setForm(emptyForm(user));
      setPendingFiles([]);
      setPendingSectionFiles(emptyPendingSectionFiles());
      setPendingRowFiles(emptyPendingRowFiles());
      await load();
      setDetail(created);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create application', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPendingFiles = (files) => {
    setPendingFiles((prev) => [...prev, ...files]);
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddPendingSectionFiles = (section, files) => {
    setPendingSectionFiles((prev) => ({
      ...prev,
      [section]: [...(prev[section] || []), ...files]
    }));
  };

  const handleRemovePendingSectionFile = (section, index) => {
    setPendingSectionFiles((prev) => ({
      ...prev,
      [section]: (prev[section] || []).filter((_, i) => i !== index)
    }));
  };

  const handleUploadApplicationAttachment = async (files) => {
    if (!detail?._id || !files?.length) return;
    setSaving(true);
    try {
      const updated = await uploadApplicationAttachments(API_BASE, detail._id, files);
      if (updated) setDetail(updated);
      showToast(files.length > 1 ? 'Attachments uploaded' : 'Attachment uploaded', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteApplicationAttachment = async (att) => {
    if (!detail?._id || !att?._id) return;
    setSaving(true);
    try {
      const updated = await deleteApplicationAttachment(API_BASE, detail._id, att._id);
      setDetail(updated);
      showToast('Attachment removed', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove attachment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadSectionAttachment = async (section, files) => {
    if (!detail?._id || !files?.length) return;
    setSaving(true);
    try {
      const updated = await uploadSectionAttachments(API_BASE, detail._id, section, files);
      if (updated) setDetail(updated);
      showToast(files.length > 1 ? 'Attachments uploaded' : 'Attachment uploaded', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSectionAttachment = async (section, att) => {
    if (!detail?._id || !att?._id) return;
    setSaving(true);
    try {
      const updated = await deleteSectionAttachment(API_BASE, detail._id, section, att._id);
      setDetail(updated);
      showToast('Attachment removed', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove attachment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRowFiles = async (section, index, files, row) => {
    if (!files?.length) return;
    if (detail?._id && row?._id) {
      setSaving(true);
      try {
        const updated = await uploadRowAttachments(
          API_BASE,
          detail._id,
          section,
          row._id,
          files
        );
        if (updated) setDetail(updated);
        showToast(files.length > 1 ? 'Attachments uploaded' : 'Attachment uploaded', 'success');
        await load();
      } catch (err) {
        showToast(err.response?.data?.message || 'Upload failed', 'error');
      } finally {
        setSaving(false);
      }
      return;
    }
    const key = rowPendingKey(section, index);
    setPendingRowFiles((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), ...files]
    }));
  };

  const handleRemovePendingRowFile = (section, index, fileIndex) => {
    const key = rowPendingKey(section, index);
    setPendingRowFiles((prev) => {
      const next = { ...prev };
      next[key] = (next[key] || []).filter((_, i) => i !== fileIndex);
      if (!next[key].length) delete next[key];
      return next;
    });
  };

  const handleRemoveRowIndex = (section, removedIndex) => {
    setPendingRowFiles((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, files]) => {
        const [sec, idxStr] = key.split(':');
        if (sec !== section) {
          next[key] = files;
          return;
        }
        const idx = Number(idxStr);
        if (idx === removedIndex) return;
        const newIdx = idx > removedIndex ? idx - 1 : idx;
        next[rowPendingKey(section, newIdx)] = files;
      });
      return next;
    });
  };

  const handleDeleteRowAttachment = async (section, row, att) => {
    if (!detail?._id || !row?._id || !att?._id) return;
    setSaving(true);
    try {
      const updated = await deleteRowAttachment(
        API_BASE,
        detail._id,
        section,
        row._id,
        att._id
      );
      setDetail(updated);
      showToast('Attachment removed', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove attachment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const res = await axios.put(`/api/certification-authorization/${detail._id}`, {
        applicantName: detail.applicantName,
        applicationType: detail.applicationType,
        employeeType: detail.employeeType,
        employeeId: detail.employeeId,
        designation: detail.designation,
        aircraftEngineTypesRequired: detail.aircraftEngineTypesRequired,
        scopeOfAuthorizationSought: detail.scopeOfAuthorizationSought,
        ratingsWithLimitations: detail.ratingsWithLimitations,
        qualification: detail.qualification,
        recentExperience: detail.recentExperience,
        trainingDetails: detail.trainingDetails,
        applicantDeclarationDate: detail.applicantDeclarationDate,
        applicantDeclarationPlace: detail.applicantDeclarationPlace
      });
      let saved = res.data;
      if (Object.keys(pendingRowFiles).length) {
        const withRows = await uploadPendingRowAttachments(
          API_BASE,
          saved._id,
          saved,
          pendingRowFiles
        );
        if (withRows) saved = withRows;
        setPendingRowFiles(emptyPendingRowFiles());
      }
      setDetail(saved);
      showToast('Application saved', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const res = await axios.post(`/api/certification-authorization/${detail._id}/submit`);
      setDetail(res.data);
      showToast('Submitted to MRO Manager', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Submit failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleManagerDecide = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `/api/certification-authorization/${detail._id}/mro-manager/decide`,
        managerDecision
      );
      setDetail(res.data);
      showToast(
        managerDecision.decision === 'ACCEPTED'
          ? 'Accepted — forwarded to MRO QM'
          : 'Returned to applicant',
        managerDecision.decision === 'ACCEPTED' ? 'success' : 'warning'
      );
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Decision failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMroDecide = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `/api/certification-authorization/${detail._id}/mro-qm/decide`,
        mroDecision
      );
      setDetail(res.data);
      showToast(
        mroDecision.decision === 'ACCEPTED' ? 'Accepted — create assessment board next' : 'Returned to applicant',
        mroDecision.decision === 'ACCEPTED' ? 'success' : 'warning'
      );
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Decision failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateBoard = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const members = [
        {
          seatRole: boardForm.seat2Role,
          name: boardForm.seat2Name,
          userId: boardForm.seat2UserId || undefined,
          designation: boardForm.seat2Role
        },
        {
          seatRole: 'AME',
          name: boardForm.seat3Name,
          userId: boardForm.seat3UserId || undefined,
          designation: 'AME',
          ameLicenseNo: boardForm.seat3AmeLic
        }
      ];
      const res = await axios.post(`/api/certification-authorization/${detail._id}/board`, { members });
      setDetail(res.data);
      showToast('Assessment board created', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Board creation failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveCompetencyPayload = (extra = {}) => {
    const ca = detail?.competencyAssessment || {};
    return {
      purpose: ca.purpose,
      designation: ca.designation,
      dateOfApplication: ca.dateOfApplication,
      venue: ca.venue,
      assessmentDate: ca.assessmentDate,
      conclusionRemarks: ca.conclusionRemarks,
      overallSuitability: ca.overallSuitability,
      boardResult: ca.boardResult,
      mroQmRemarks: ca.mroQmRemarks,
      ameBoardSignature: ca.ameBoardSignature,
      ameLicenseNo: ca.ameLicenseNo,
      lmmBmmBoardSignature: ca.lmmBmmBoardSignature,
      cmmSignature: ca.cmmSignature,
      rows: ca.rows,
      ...extra
    };
  };

  const handleSaveCompetency = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `/api/certification-authorization/${detail._id}/competency`,
        saveCompetencyPayload()
      );
      setDetail(res.data);
      showToast('Competency assessment saved', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save assessment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAssessmentComplete = async () => {
    if (!detail?._id) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `/api/certification-authorization/${detail._id}/competency`,
        saveCompetencyPayload({
          markComplete: true,
          assessmentDate:
            detail.competencyAssessment?.assessmentDate || dayjs().format('YYYY-MM-DD')
        })
      );
      setDetail(res.data);
      showToast('Competency assessment marked complete', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to complete assessment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadApplicationPdf = async () => {
    if (!detail?._id) return;
    try {
      await downloadAuthenticatedPdf(
        `/api/certification-authorization/${detail._id}/application/pdf`,
        `GETS-CM-008_${(detail.applicantName || 'applicant').replace(/\s+/g, '_')}.pdf`
      );
    } catch (err) {
      showToast(err.message || 'Failed to download application PDF', 'error');
    }
  };

  const handleDownloadCompetencyPdf = async () => {
    if (!detail?._id) return;
    try {
      await downloadAuthenticatedPdf(
        `/api/certification-authorization/${detail._id}/competency/pdf`,
        `GETS-CM-003_${(detail.applicantName || 'applicant').replace(/\s+/g, '_')}.pdf`
      );
    } catch (err) {
      showToast(err.message || 'Failed to download competency PDF', 'error');
    }
  };

  const handleUploadCompetencyAttachment = async (file) => {
    if (!detail?._id || !file) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `/api/certification-authorization/${detail._id}/competency/attachments`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setDetail(res.data);
      showToast('Document uploaded', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadCompetencyAttachment = async (att) => {
    if (!att?.fileUrl) return;
    try {
      await downloadApplicationAttachment(att);
    } catch (err) {
      showToast(err.message || 'Failed to download document', 'error');
    }
  };

  const handleDeleteCompetencyAttachment = async (att) => {
    if (!detail?._id || !att?._id) return;
    setSaving(true);
    try {
      const res = await axios.delete(
        `/api/certification-authorization/${detail._id}/competency/attachments/${att._id}`
      );
      setDetail(res.data);
      showToast('Document removed', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove document', 'error');
    } finally {
      setSaving(false);
    }
  };

  const buildCertificatePayload = (cert, issued) => ({
    issued: Boolean(issued),
    authorizationNo: cert.authorizationNo,
    caRevisionNo: cert.caRevisionNo,
    caRevisionDate: cert.caRevisionDate || dayjs().format('YYYY-MM-DD'),
    dateOfInitialCaIssue: cert.dateOfInitialCaIssue || dayjs().format('YYYY-MM-DD'),
    validFrom: cert.validFrom,
    validUntil: cert.validUntil,
    category: cert.category,
    dateOfBirth: cert.dateOfBirth,
    licenseNumber: cert.licenseNumber,
    licenseExpiry: cert.licenseExpiry,
    recurrentTrainingValidTill: cert.recurrentTrainingValidTill,
    limitations: cert.limitations,
    scopeRows: cert.scopeRows,
    dgcaApprovalNo: cert.dgcaApprovalNo,
    dgcaApprovalDated: cert.dgcaApprovalDated,
    dgcaApprovalValidity: cert.dgcaApprovalValidity
  });

  const ensureCertificateDraft = (detailDoc) => {
    const existing = detailDoc?.certificate || {};
    return emptyGetsCm001Certificate({
      ...existing,
      licenseNumber:
        existing.licenseNumber || detailDoc?.qualification?.licenseNumber || '',
      licenseExpiry:
        existing.licenseExpiry ||
        (detailDoc?.qualification?.validity
          ? String(detailDoc.qualification.validity).slice(0, 10)
          : ''),
      validFrom: existing.validFrom
        ? String(existing.validFrom).slice(0, 10)
        : dayjs().format('YYYY-MM-DD'),
      validUntil: existing.validUntil
        ? String(existing.validUntil).slice(0, 10)
        : dayjs().add(1, 'year').format('YYYY-MM-DD'),
      dateOfInitialCaIssue: existing.dateOfInitialCaIssue
        ? String(existing.dateOfInitialCaIssue).slice(0, 10)
        : dayjs().format('YYYY-MM-DD'),
      caRevisionDate: existing.caRevisionDate
        ? String(existing.caRevisionDate).slice(0, 10)
        : dayjs().format('YYYY-MM-DD'),
      dgcaApprovalDated: existing.dgcaApprovalDated
        ? String(existing.dgcaApprovalDated).slice(0, 10)
        : '2026-10-13',
      dgcaApprovalValidity: existing.dgcaApprovalValidity
        ? String(existing.dgcaApprovalValidity).slice(0, 10)
        : '2031-10-12',
      scopeRows:
        Array.isArray(existing.scopeRows) && existing.scopeRows.length
          ? existing.scopeRows
          : undefined
    });
  };

  const handleIssue = async (issued) => {
    if (!detail?._id) return;
    const cert = detail.certificate || ensureCertificateDraft(detail);
    setSaving(true);
    try {
      const res = await axios.post(
        `/api/certification-authorization/${detail._id}/issue`,
        buildCertificatePayload(cert, issued)
      );
      setDetail(res.data);
      showToast(issued ? 'Authorization issued (GETS/CM/001)' : 'Marked not issued', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Issue failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadCertificatePdf = async () => {
    if (!detail?._id) return;
    try {
      // Persist current draft fields first so PDF matches the form
      if (canMroQm && detail.certificate) {
        await axios.put(
          `/api/certification-authorization/${detail._id}/certificate`,
          buildCertificatePayload(detail.certificate, detail.certificate.issued)
        );
      }
      const authNo = (detail.certificate?.authorizationNo || 'GETS-CA').replace(/\s+/g, '_');
      const name = (detail.applicantName || 'authorization').replace(/\s+/g, '_');
      await downloadAuthenticatedPdf(
        `/api/certification-authorization/${detail._id}/certificate/pdf`,
        `GETS-CM-001_${authNo}_${name}.pdf`
      );
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Failed to download certificate PDF', 'error');
    }
  };

  const editableStatuses = useMemo(
    () => ['DRAFT', 'RETURNED_TO_APPLICANT'],
    []
  );
  const currentUserId = user?._id || user?.id;
  const isApplicantOwner =
    detail &&
    currentUserId &&
    String(detail.applicantUserId?._id || detail.applicantUserId) === String(currentUserId);
  const canEditDetail =
    detail &&
    editableStatuses.includes(detail.status) &&
    (isApplicantOwner ||
      [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(user?.role));
  const canManageAttachments =
    Boolean(detail) &&
    (canEditDetail ||
      canMroQm ||
      [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER].includes(user?.role));

  const subtitle =
    variant === 'crew'
      ? 'Apply for issue / extension / renewal of authorization (GETS/CM/008).'
      : variant === 'mro'
        ? 'Engineer & manager applications; MRO QM review, board, and issue.'
        : `${FORM_REFS.application} → ${FORM_REFS.competency} → ${FORM_REFS.certificate}`;

  return (
    <Box sx={{ p: embedded ? 0 : 2 }}>
      {!embedded && (
        <GetsBrandHeader title="Authorization" subtitle={subtitle} />
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        {embedded ? (
          <Box>
            <Typography variant="h6">Authorization</Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        ) : (
          <Box />
        )}
        <Stack direction="row" spacing={1}>
          <IconButton onClick={load} title="Refresh">
            <Refresh />
          </IconButton>
          {canApply && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setForm(emptyForm(user));
                setPendingFiles([]);
                setPendingSectionFiles(emptyPendingSectionFiles());
                setPendingRowFiles(emptyPendingRowFiles());
                setCreateOpen(true);
              }}
            >
              New application
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Applicant</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Auth No.</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Open</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6}>Loading…</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>No applications yet</TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>
                    {row.applicantName ||
                      `${row.applicantUserId?.firstName || ''} ${row.applicantUserId?.lastName || ''}`.trim() ||
                      '—'}
                  </TableCell>
                  <TableCell>{row.applicationType}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_LABELS[row.status] || row.status}
                      color={STATUS_COLORS[row.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>{row.certificate?.authorizationNo || '—'}</TableCell>
                  <TableCell>{row.updatedAt ? dayjs(row.updatedAt).format('YYYY-MM-DD HH:mm') : '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openDetail(row._id)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create dialog — full GETS/CM/008 Page 1 */}
      <Dialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setPendingFiles([]);
          setPendingSectionFiles(emptyPendingSectionFiles());
          setPendingRowFiles(emptyPendingRowFiles());
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>New application — {FORM_REFS.application}</DialogTitle>
        <DialogContent dividers>
          <GetsCm008ApplicationForm
            value={form}
            onChange={setForm}
            pendingFiles={pendingFiles}
            attachmentsSaving={saving}
            onAddAttachmentFiles={handleAddPendingFiles}
            onRemovePendingAttachment={handleRemovePendingFile}
            pendingSectionFiles={pendingSectionFiles}
            onAddSectionFiles={handleAddPendingSectionFiles}
            onRemovePendingSectionFile={handleRemovePendingSectionFile}
            pendingRowFiles={pendingRowFiles}
            onAddRowFiles={handleAddRowFiles}
            onRemovePendingRowFile={handleRemovePendingRowFile}
            onRemoveRowIndex={handleRemoveRowIndex}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateOpen(false);
              setPendingFiles([]);
              setPendingSectionFiles(emptyPendingSectionFiles());
              setPendingRowFiles(emptyPendingRowFiles());
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" disabled={saving} onClick={handleCreate}>
            Create draft
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail — three separate documents */}
      <Dialog
        open={Boolean(detail)}
        onClose={() => {
          setDetail(null);
          setDocTab(0);
          setPendingRowFiles(emptyPendingRowFiles());
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
          <span>Authorization — documents</span>
          <IconButton
            onClick={() => {
              setDetail(null);
              setDocTab(0);
              setPendingRowFiles(emptyPendingRowFiles());
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={STATUS_LABELS[detail.status] || detail.status}
                  color={STATUS_COLORS[detail.status] || 'default'}
                />
                <Typography variant="body2">{detail.applicationType}</Typography>
              </Stack>

              <Tabs
                value={docTab}
                onChange={(_, v) => setDocTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label={`1. Application (${FORM_REFS.application})`} />
                <Tab
                  label={`2. Competency (${FORM_REFS.competency})`}
                  disabled={
                    ![
                      'BOARD_FORMED',
                      'ASSESSMENT_IN_PROGRESS',
                      'ASSESSMENT_COMPLETE',
                      'ISSUED',
                      'NOT_ISSUED'
                    ].includes(detail.status)
                  }
                />
                <Tab
                  label={`3. Authorization (${FORM_REFS.certificate})`}
                  disabled={
                    !(
                      (canMroQm || detail.status === 'ISSUED' || detail.status === 'NOT_ISSUED') &&
                      [
                        'ASSESSMENT_COMPLETE',
                        'ASSESSMENT_IN_PROGRESS',
                        'BOARD_FORMED',
                        'ISSUED',
                        'NOT_ISSUED'
                      ].includes(detail.status)
                    )
                  }
                />
              </Tabs>

              {docTab === 0 && (
                <Stack spacing={2}>
                  <GetsCm008ApplicationForm
                    value={detail}
                    onChange={(next) => setDetail((d) => ({ ...d, ...next }))}
                    readOnly={!canEditDetail}
                    attachmentsReadOnly={!canManageAttachments}
                    onDownloadPdf={handleDownloadApplicationPdf}
                    attachments={detail.applicationAttachments || []}
                    attachmentsSaving={saving}
                    onAddAttachmentFiles={
                      canManageAttachments ? handleUploadApplicationAttachment : undefined
                    }
                    onViewAttachment={viewApplicationAttachment}
                    onDownloadAttachment={downloadApplicationAttachment}
                    onDeleteAttachment={
                      canManageAttachments ? handleDeleteApplicationAttachment : undefined
                    }
                    sectionAttachments={getSectionAttachmentsFromDoc(detail)}
                    onAddSectionFiles={
                      canManageAttachments ? handleUploadSectionAttachment : undefined
                    }
                    onDeleteSectionAttachment={
                      canManageAttachments ? handleDeleteSectionAttachment : undefined
                    }
                    pendingRowFiles={pendingRowFiles}
                    onAddRowFiles={canManageAttachments ? handleAddRowFiles : undefined}
                    onRemovePendingRowFile={
                      canManageAttachments ? handleRemovePendingRowFile : undefined
                    }
                    onDeleteRowAttachment={
                      canManageAttachments ? handleDeleteRowAttachment : undefined
                    }
                    onRemoveRowIndex={canManageAttachments ? handleRemoveRowIndex : undefined}
                  />

                  {canEditDetail && (
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" disabled={saving} onClick={handleSaveDraft}>
                        Save
                      </Button>
                      <Button variant="contained" disabled={saving} onClick={handleSubmit}>
                        Submit to MRO Manager
                      </Button>
                    </Stack>
                  )}

                  {canMroManager && detail.status === 'SUBMITTED' && (
                    <>
                      <Divider />
                      <Typography variant="subtitle1">MRO Manager review</Typography>
                      <TextField
                        select
                        fullWidth
                        label="Decision"
                        value={managerDecision.decision}
                        onChange={(e) =>
                          setManagerDecision((d) => ({ ...d, decision: e.target.value }))
                        }
                      >
                        <MenuItem value="ACCEPTED">Accept (forward to MRO QM)</MenuItem>
                        <MenuItem value="REJECTED">Reject (return to applicant)</MenuItem>
                      </TextField>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Remarks"
                        value={managerDecision.remarks}
                        onChange={(e) =>
                          setManagerDecision((d) => ({ ...d, remarks: e.target.value }))
                        }
                      />
                      <Button variant="contained" disabled={saving} onClick={handleManagerDecide}>
                        Submit MRO Manager decision
                      </Button>
                    </>
                  )}

                  {detail.mroManagerCheck?.decision && detail.status !== 'SUBMITTED' && (
                    <Alert
                      severity={
                        detail.mroManagerCheck.decision === 'ACCEPTED' ? 'success' : 'warning'
                      }
                    >
                      MRO Manager: {detail.mroManagerCheck.decision}
                      {detail.mroManagerCheck.remarks
                        ? ` — ${detail.mroManagerCheck.remarks}`
                        : ''}
                    </Alert>
                  )}

                  {canMroQm && detail.status === 'ACCEPTED_BY_MRO_MANAGER' && (
                    <>
                      <Divider />
                      <Typography variant="subtitle1">MRO QM / CMD check</Typography>
                      <TextField
                        select
                        fullWidth
                        label="Decision"
                        value={mroDecision.decision}
                        onChange={(e) => setMroDecision((d) => ({ ...d, decision: e.target.value }))}
                      >
                        <MenuItem value="ACCEPTED">Accept</MenuItem>
                        <MenuItem value="REJECTED">Reject (return to applicant)</MenuItem>
                      </TextField>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Remarks"
                        value={mroDecision.remarks}
                        onChange={(e) => setMroDecision((d) => ({ ...d, remarks: e.target.value }))}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={mroDecision.competencyAssessmentRequired}
                            onChange={(e) =>
                              setMroDecision((d) => ({
                                ...d,
                                competencyAssessmentRequired: e.target.checked
                              }))
                            }
                          />
                        }
                        label="Competency assessment required"
                      />
                      <Button variant="contained" disabled={saving} onClick={handleMroDecide}>
                        Submit MRO QM decision
                      </Button>
                    </>
                  )}

                  {canMroQm && detail.status === 'ACCEPTED_BY_MRO_QM' && (
                    <>
                      <Divider />
                      <Typography variant="subtitle1">Create assessment board (MRO QM)</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Head of board = you (MRO QM / CMM). Select LMM/BMM and AME from MRO, CAMO Manager, CAMO QM,
                        Accountable Manager, or Crew — or type a name if they are not in the system.
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4} md={3}>
                          <TextField
                            select
                            fullWidth
                            label="Seat 2 role"
                            value={boardForm.seat2Role}
                            onChange={(e) => setBoardForm((b) => ({ ...b, seat2Role: e.target.value }))}
                          >
                            <MenuItem value="LMM">LMM</MenuItem>
                            <MenuItem value="BMM">BMM</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <Autocomplete
                            {...boardAutocompleteWideProps}
                            freeSolo
                            options={boardCandidates}
                            filterOptions={boardUserFilter}
                            groupBy={(option) => ROLE_DISPLAY_NAMES[option.role] || option.role}
                            getOptionLabel={(option) =>
                              typeof option === 'string' ? option : formatBoardUserLabel(option)
                            }
                            isOptionEqualToValue={(a, b) =>
                              a &&
                              b &&
                              typeof a !== 'string' &&
                              typeof b !== 'string' &&
                              String(a._id) === String(b._id)
                            }
                            value={boardForm.seat2User || boardForm.seat2Name || null}
                            onChange={(_, value) => applyBoardUserSelection('seat2', value)}
                            onInputChange={(_, inputValue, reason) => {
                              if (reason === 'input') {
                                setBoardForm((b) => ({
                                  ...b,
                                  seat2User: null,
                                  seat2UserId: '',
                                  seat2Name: inputValue
                                }));
                              }
                            }}
                            renderOption={(props, option) => {
                              const { key, ...optionProps } = props;
                              const label =
                                typeof option === 'string'
                                  ? option
                                  : formatBoardUserLabel(option);
                              return (
                                <li key={key || option._id || label} {...optionProps}>
                                  {label}
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                label="LMM / BMM — select user or type name"
                                placeholder="Search MRO / CAMO / AM / Crew…"
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Autocomplete
                            {...boardAutocompleteWideProps}
                            freeSolo
                            options={boardCandidates}
                            filterOptions={boardUserFilter}
                            groupBy={(option) => ROLE_DISPLAY_NAMES[option.role] || option.role}
                            getOptionLabel={(option) =>
                              typeof option === 'string' ? option : formatBoardUserLabel(option)
                            }
                            isOptionEqualToValue={(a, b) =>
                              a &&
                              b &&
                              typeof a !== 'string' &&
                              typeof b !== 'string' &&
                              String(a._id) === String(b._id)
                            }
                            value={boardForm.seat3User || boardForm.seat3Name || null}
                            onChange={(_, value) => applyBoardUserSelection('seat3', value)}
                            onInputChange={(_, inputValue, reason) => {
                              if (reason === 'input') {
                                setBoardForm((b) => ({
                                  ...b,
                                  seat3User: null,
                                  seat3UserId: '',
                                  seat3Name: inputValue
                                }));
                              }
                            }}
                            renderOption={(props, option) => {
                              const { key, ...optionProps } = props;
                              const label =
                                typeof option === 'string'
                                  ? option
                                  : formatBoardUserLabel(option);
                              return (
                                <li key={key || option._id || label} {...optionProps}>
                                  {label}
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                fullWidth
                                label="AME — select user or type name"
                                placeholder="Search MRO / CAMO / AM / Crew…"
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            label="AME license no."
                            value={boardForm.seat3AmeLic}
                            onChange={(e) => setBoardForm((b) => ({ ...b, seat3AmeLic: e.target.value }))}
                          />
                        </Grid>
                      </Grid>
                      <Button variant="contained" disabled={saving} onClick={handleCreateBoard}>
                        Create board
                      </Button>
                    </>
                  )}

                  {detail.board?.members?.length > 0 && (
                    <>
                      <Divider />
                      <Typography variant="subtitle2">Board</Typography>
                      {detail.board.members.map((m, idx) => (
                        <Typography key={idx} variant="body2">
                          {m.seatRole}: {m.name || m.userId?.firstName || '—'}
                          {m.ameLicenseNo ? ` (${m.ameLicenseNo})` : ''}
                        </Typography>
                      ))}
                    </>
                  )}
                </Stack>
              )}

              {docTab === 1 && (
                <GetsCm003CompetencyForm
                  application={detail}
                  value={{
                    ...(detail.competencyAssessment || {}),
                    rows: detail.competencyAssessment?.rows?.length
                      ? detail.competencyAssessment.rows
                      : emptyCompetencyRows(),
                    attachments: detail.competencyAssessment?.attachments || []
                  }}
                  onChange={(next) =>
                    setDetail((d) => ({
                      ...d,
                      competencyAssessment: { ...(d.competencyAssessment || {}), ...next }
                    }))
                  }
                  readOnly={
                    !(
                      (canMroQm ||
                        (detail.board?.members || []).some(
                          (m) =>
                            m.userId &&
                            String(m.userId._id || m.userId) === String(user?._id || user?.id)
                        )) &&
                      ['BOARD_FORMED', 'ASSESSMENT_IN_PROGRESS', 'ASSESSMENT_COMPLETE'].includes(
                        detail.status
                      )
                    )
                  }
                  saving={saving}
                  onSave={
                    (canMroQm ||
                      (detail.board?.members || []).some(
                        (m) =>
                          m.userId &&
                          String(m.userId._id || m.userId) === String(user?._id || user?.id)
                      )) &&
                    ['BOARD_FORMED', 'ASSESSMENT_IN_PROGRESS', 'ASSESSMENT_COMPLETE'].includes(
                      detail.status
                    )
                      ? handleSaveCompetency
                      : undefined
                  }
                  onMarkComplete={
                    (canMroQm ||
                      (detail.board?.members || []).some(
                        (m) =>
                          m.userId &&
                          String(m.userId._id || m.userId) === String(user?._id || user?.id)
                      )) &&
                    ['BOARD_FORMED', 'ASSESSMENT_IN_PROGRESS'].includes(detail.status)
                      ? handleMarkAssessmentComplete
                      : undefined
                  }
                  onDownloadPdf={handleDownloadCompetencyPdf}
                  onUploadAttachment={
                    (canMroQm ||
                      (detail.board?.members || []).some(
                        (m) =>
                          m.userId &&
                          String(m.userId._id || m.userId) === String(user?._id || user?.id)
                      )) &&
                    ['BOARD_FORMED', 'ASSESSMENT_IN_PROGRESS', 'ASSESSMENT_COMPLETE'].includes(
                      detail.status
                    )
                      ? handleUploadCompetencyAttachment
                      : undefined
                  }
                  onViewAttachment={viewApplicationAttachment}
                  onDownloadAttachment={handleDownloadCompetencyAttachment}
                  onDeleteAttachment={
                    (canMroQm ||
                      (detail.board?.members || []).some(
                        (m) =>
                          m.userId &&
                          String(m.userId._id || m.userId) === String(user?._id || user?.id)
                      )) &&
                    ['BOARD_FORMED', 'ASSESSMENT_IN_PROGRESS', 'ASSESSMENT_COMPLETE'].includes(
                      detail.status
                    )
                      ? handleDeleteCompetencyAttachment
                      : undefined
                  }
                />
              )}

              {docTab === 2 && (
                <Stack spacing={2}>
                  {detail.status === 'ISSUED' && detail.certificate?.issued && (
                    <Alert severity="success">
                      Issued {detail.certificate.authorizationNo} ·{' '}
                      {detail.certificate.validFrom
                        ? dayjs(detail.certificate.validFrom).format('DD-MM-YYYY')
                        : '?'}{' '}
                      →{' '}
                      {detail.certificate.validUntil
                        ? dayjs(detail.certificate.validUntil).format('DD-MM-YYYY')
                        : '?'}
                    </Alert>
                  )}
                  <GetsCm001CertificateForm
                    application={detail}
                    value={ensureCertificateDraft(detail)}
                    onChange={(next) =>
                      setDetail((d) => ({
                        ...d,
                        certificate: { ...(d.certificate || {}), ...next }
                      }))
                    }
                    readOnly={
                      !canMroQm ||
                      !['ASSESSMENT_COMPLETE', 'ASSESSMENT_IN_PROGRESS', 'BOARD_FORMED'].includes(
                        detail.status
                      )
                    }
                    saving={saving}
                    onIssue={
                      canMroQm &&
                      ['ASSESSMENT_COMPLETE', 'ASSESSMENT_IN_PROGRESS', 'BOARD_FORMED'].includes(
                        detail.status
                      )
                        ? handleIssue
                        : undefined
                    }
                    onDownloadPdf={handleDownloadCertificatePdf}
                  />
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default CertificationAuthorizationPage;
